import { PayloadAction } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import * as transactionsMiddleware from "middleware/transactions";
import { actions } from "reducers/detailTransaction";
import { actions as transactionActions } from "reducers/transactions";
import { store } from "../store";
import {
  call,
  CallEffect,
  ForkEffect,
  put,
  PutEffect,
  takeLatest,
} from "redux-saga/effects";
import {
  DetailTransactionResponseType,
  DevolutionUndeliveredResponseType,
  PayloadTransactionType,
  PayloadDniDeliveredType,
  postCancelType,
  postDevolutionUndeliveredType,
  RelationshipResponse,
  TransactionCancelResponseType,
  DeliveryProofFileResponseType,
} from "./types/detailTransactions";
import { actions as notificationActions } from "../reducers/notification";
import { serverErrorAlert } from "utils/alerts";
import { ApiResponse, ApiResponseResult } from "middleware/api";
import { watermarkImageWithText } from "utils/waterMark";
import {
  deliveryProofType,
  deliveryProofSignType,
  deliveryProofFileType,
} from "pages/transaction/detailTransaction/dniFinish/types";
import { getValue } from "utils/utils";
import {
  PostDeliveryProofResponseType,
  ProofType,
} from "./types/detailTransactions";
import i18next from "config/i18next";

const sagas: ForkEffect<never>[] = [
  takeLatest(actions.getDetailTransactionRequest.type, getDetailTransaction),
  takeLatest(actions.getDetailTransactionMenssagesRequest.type, getMessages),
  takeLatest(
    actions.getDetailTransactionDevolutionUndeliveredRequest.type,
    postDevolutionUndelivered
  ),
  takeLatest(
    actions.getDetailTransactionReasonsCanceledRequest.type,
    postReasonsCanceled
  ),
  takeLatest(
    actions.getDetailTransactionFinishReturnedRequest.type,
    postFinishReturned
  ),
  takeLatest(
    actions.getDetailTransactionFinishLostRequest.type,
    postFinishLost
  ),
  takeLatest(
    actions.getDetailTransactionDniDeliveredRequest.type,
    postDnidelivered
  ),
  takeLatest(actions.getProofRequest.type, getProof),
  takeLatest(actions.postDeliveryProofRequest.type, postDeliveryProof),
  takeLatest(actions.postDeliveryProofSignRequest.type, postDeliveryProofSign),
  takeLatest(actions.postDeliveryProofFileRequest.type, postDeliveryProofFile),
  takeLatest(actions.getRelationshipsRequest.type, getRelationship),
];

const errorsCodes = [20026, 20027, 20028, 20029, 20030];

function getTokenPayload(): { user_id?: number; userId?: number; rol?: string; role?: string } {
  try {
    const state: { session?: { countryCode?: string } } = store.getState();
    const countryCode = state.session?.countryCode;
    const tokens = getValue("tokens", true) || {};
    const token = countryCode ? tokens[countryCode] : null;
    if (!token) return {};
    const parts = token.split(".");
    if (parts.length !== 3) return {};
    return JSON.parse(atob(parts[1])) || {};
  } catch {
    return {};
  }
}

function* getDetailTransaction({
  payload,
}: PayloadAction<string>): Generator<
  | CallEffect<AxiosResponse<DetailTransactionResponseType>>
  | PutEffect<{ type: string }>,
  void,
  DetailTransactionResponseType
> {
  const response = yield call(
    transactionsMiddleware.getDetailTransaction,
    payload
  );
  if (response.status !== 200) {
    yield put(actions.getDetailTransactionError());
    yield put(notificationActions.showNotification(serverErrorAlert()));
  } else {
    const { result } = response.data;
    yield put(actions.getDetailTransactionSuccess(result));
  }
}

function* getMessages({
  payload,
}: PayloadAction<PayloadTransactionType>): Generator<
  | CallEffect<AxiosResponse<TransactionCancelResponseType>>
  | PutEffect<{ type: string }>,
  void,
  TransactionCancelResponseType
> {
  const response = yield call(transactionsMiddleware.getMessages, payload.id);
  if (response.status !== 200) {
    if (
      response.data.statusCode &&
      errorsCodes.includes(response.data.statusCode)
    ) {
      yield put(
        notificationActions.showNotification({
          id: "notification-error-delivery-proof",
          level: "error",
          title: i18next.t("detailTransaction:deliveryProofErrorTitle"),
          body: i18next.t("detailTransaction:deliveryProofErrorBody"),
          onConfirmLabel: i18next.t("global:understood"),
          onClose: payload?.callBack,
        })
      );
    } else {
      yield put(
        notificationActions.showNotification(
          serverErrorAlert(payload?.callBack)
        )
      );
    }
    yield put(actions.getDetailTransactionMenssagesError());
  } else {
    const { result } = response.data;
    yield put(actions.getDetailTransactionMenssagesSuccess(result.items));
  }
}

function* postDevolutionUndelivered({
  payload,
}: PayloadAction<PayloadTransactionType<number>>): Generator<
  | CallEffect<AxiosResponse<DevolutionUndeliveredResponseType>>
  | PutEffect<{ type: string }>
  | void,
  void,
  DevolutionUndeliveredResponseType
> {
  const { filters } = store.getState().transactions;

  const paramsPost: postDevolutionUndeliveredType = {
    impossibleDeliveryReasonId: Number(payload.params),
  };
  const response = yield call(
    transactionsMiddleware.postDevolutionUndelivered,
    paramsPost,
    payload.id
  );
  if (response.status !== 200) {
    if (
      response.data.statusCode &&
      errorsCodes.includes(response.data.statusCode)
    ) {
      yield put(
        notificationActions.showNotification({
          id: "notification-error-delivery-proof",
          level: "error",
          title: i18next.t("detailTransaction:deliveryProofErrorTitle"),
          body: i18next.t("detailTransaction:deliveryProofErrorBody"),
          onConfirmLabel: i18next.t("global:understood"),

          onClose: payload?.callBack,
        })
      );
    } else {
      yield put(
        notificationActions.showNotification(
          serverErrorAlert(payload?.callBack)
        )
      );
    }
    yield put(actions.getDetailTransactionDevolutionUndeliveredError());
  } else {
    yield put(
      transactionActions.getTransactionsRequest({
        ...filters,
      })
    );
    yield put(
      transactionActions.getDetailTransactionDevolutionUndeliveredSuccess()
    );
  }
}

function* postReasonsCanceled({
  payload,
}: PayloadAction<PayloadTransactionType<postCancelType>>): Generator<
  | CallEffect<AxiosResponse<DevolutionUndeliveredResponseType>>
  | PutEffect<{ type: string }>
  | void,
  void,
  DevolutionUndeliveredResponseType
> {
  const { filters } = store.getState().transactions;

  const paramsPost: postCancelType = {
    cancellationReasonId: parseInt(payload.params + ""),
  };

  const response = yield call(
    transactionsMiddleware.postReasonsCanceled,
    paramsPost,
    payload.id
  );
  if (response.status !== 200) {
    if (
      response.data.statusCode &&
      errorsCodes.includes(response.data.statusCode)
    ) {
      yield put(
        notificationActions.showNotification({
          id: "notification-error-delivery-proof",
          level: "error",
          title: i18next.t("detailTransaction:deliveryProofErrorTitle"),
          body: i18next.t("detailTransaction:deliveryProofErrorBody"),
          onConfirmLabel: i18next.t("global:understood"),

          onClose: payload?.callBack,
        })
      );
    } else {
      yield put(
        notificationActions.showNotification(
          serverErrorAlert(payload?.callBack)
        )
      );
    }
    yield put(actions.getDetailTransactionReasonsCanceledError());
    // TOOD: chjequear callback
  } else {
    yield put(
      transactionActions.getTransactionsRequest({
        ...filters,
      })
    );
    yield put(transactionActions.getDetailTransactionReasonsCanceledSuccess());
  }
}

function* postFinishReturned({
  payload,
}: PayloadAction<PayloadTransactionType>): Generator<
  | CallEffect<AxiosResponse<DevolutionUndeliveredResponseType>>
  | PutEffect<{ type: string }>
  | void,
  void,
  DevolutionUndeliveredResponseType
> {
  const { filters } = store.getState().transactions;

  const response = yield call(
    transactionsMiddleware.postFinishReturned,
    payload.id
  );
  if (response.status !== 200) {
    if (
      response.data.statusCode &&
      errorsCodes.includes(response.data.statusCode)
    ) {
      yield put(
        notificationActions.showNotification({
          id: "notification-error-delivery-proof",
          level: "error",
          title: i18next.t("detailTransaction:deliveryProofErrorTitle"),
          body: i18next.t("detailTransaction:deliveryProofErrorBody"),
          onConfirmLabel: i18next.t("global:understood"),

          onClose: payload?.callBack,
        })
      );
    } else {
      yield put(
        notificationActions.showNotification(
          serverErrorAlert(payload?.callBack)
        )
      );
    }
    yield put(actions.getDetailTransactionFinishReturnedError());
  } else {
    yield put(
      transactionActions.getTransactionsRequest({
        ...filters,
      })
    );
    yield put(transactionActions.getDetailTransactionFinishReturnedSuccess());
  }
}

function* postFinishLost({
  payload,
}: PayloadAction<PayloadTransactionType>): Generator<
  | CallEffect<AxiosResponse<DevolutionUndeliveredResponseType>>
  | PutEffect<{ type: string }>
  | void,
  void,
  DevolutionUndeliveredResponseType
> {
  const { filters } = store.getState().transactions;

  const response = yield call(
    transactionsMiddleware.postFinishLost,
    payload.id
  );
  if (response.status !== 200) {
    if (
      response.data.statusCode &&
      errorsCodes.includes(response.data.statusCode)
    ) {
      yield put(
        notificationActions.showNotification({
          id: "notification-error-delivery-proof",
          level: "error",
          title: i18next.t("detailTransaction:deliveryProofErrorTitle"),
          body: i18next.t("detailTransaction:deliveryProofErrorBody"),
          onConfirmLabel: i18next.t("global:understood"),

          onClose: payload?.callBack,
        })
      );
    } else {
      yield put(
        notificationActions.showNotification(
          serverErrorAlert(payload?.callBack)
        )
      );
    }
    yield put(actions.getDetailTransactionFinishLostError());
  } else {
    yield put(
      transactionActions.getTransactionsRequest({
        ...filters,
      })
    );
    yield put(transactionActions.getDetailTransactionFinishLostSuccess());
  }
}
function* postDnidelivered({
  payload,
}: PayloadAction<PayloadDniDeliveredType>): Generator<
  | CallEffect<AxiosResponse<DevolutionUndeliveredResponseType>>
  | PutEffect<{ type: string }>
  | void,
  void,
  DevolutionUndeliveredResponseType
> {
  const tokenPayload = getTokenPayload();
  const userId = tokenPayload.user_id ?? tokenPayload.userId ?? 0;
  const rolId = tokenPayload.rol ?? tokenPayload.role ?? "";

  const headers: transactionsMiddleware.DeliveredHeadersType = {
    "country-code": payload.countryCode ?? "",
    "x-latitude": payload.latitude != null ? String(payload.latitude) : "",
    "x-longitude": payload.longitude != null ? String(payload.longitude) : "",
    "x-user-id": String(userId),
    "x-rol-id": String(rolId),
  };

  const response = yield call(
    transactionsMiddleware.postDnidelivered,
    payload.params!,
    payload.id,
    { headers }
  );

  if (response.status !== 200) {
    if (
      response.data.statusCode &&
      errorsCodes.includes(response.data.statusCode)
    ) {
      yield put(
        notificationActions.showNotification({
          id: "notification-error-delivery-proof",
          level: "error",
          title: i18next.t("detailTransaction:deliveryProofErrorTitle"),
          body: i18next.t("detailTransaction:deliveryProofErrorBody"),
          onConfirmLabel: i18next.t("global:understood"),

          onClose: payload?.callBack,
        })
      );
    } else {
      yield put(
        notificationActions.showNotification(
          serverErrorAlert(payload?.callBack)
        )
      );
    }
    yield put(actions.getDetailTransactionDniDeliveredError());
  } else {
    payload?.callBack();
    yield put(transactionActions.getDetailTransactionDniDeliveredSuccess());
  }
}

function* postDeliveryProofFile({
  payload,
}: PayloadAction<deliveryProofFileType>): Generator<
  | CallEffect<AxiosResponse<DeliveryProofFileResponseType>>
  | PutEffect<{ type: string }>
  | void,
  void,
  AxiosResponse<DeliveryProofFileResponseType>
> {
  let responseActionDispatched = false;
  try {
    const tokenPayload = getTokenPayload();
    const userId = tokenPayload.user_id ?? tokenPayload.userId ?? 0;
    const rolId = tokenPayload.rol ?? tokenPayload.role ?? "";

    const response = yield call(
      transactionsMiddleware.postDeliveryProofFile,
      payload.id,
      payload.file,
      {
        "x-user-id": String(userId),
        "x-rol-id": String(rolId),
        "x-type": payload.xType,
      }
    );

    if (response.status !== 200 && response.status !== 201) {
      const { filters } = store.getState().transactions;
      const statusCode = (response.data as { statusCode?: number })?.statusCode;
      if (statusCode && errorsCodes.includes(statusCode)) {
        yield put(
          notificationActions.showNotification({
            id: "notification-error-delivery-proof",
            level: "error",
            title: i18next.t("detailTransaction:deliveryProofErrorTitle"),
            body: i18next.t("detailTransaction:deliveryProofErrorBody"),
            onConfirmLabel: i18next.t("global:understood"),
            onClose: () => {
              transactionActions.getTransactionsRequest({ ...filters, offset: 0 });
            },
          })
        );
      } else {
        yield put(
          notificationActions.showNotification(
            serverErrorAlert(() => {
              transactionActions.getTransactionsRequest({
                ...store.getState().transactions.filters,
                offset: 0,
              });
            })
          )
        );
      }
      responseActionDispatched = true;
      yield put(actions.postDeliveryProofFileError());
    } else {
      const data = response.data as { path?: string; result?: { path?: string }; photo?: string; sign?: string };
      const path = data?.path ?? data?.result?.path;
      const keys: DeliveryProofFileResponseType =
        path != null && payload.xType
          ? { [payload.xType]: path }
          : { photo: data?.photo, sign: data?.sign };
      responseActionDispatched = true;
      yield put(actions.postDeliveryProofFileSuccess());
      try {
        payload.callBack(keys);
      } catch {}
    }
  } catch {
    if (!responseActionDispatched) {
      yield put(actions.postDeliveryProofFileError());
    }
    yield put(
      notificationActions.showNotification(
        serverErrorAlert(() => {
          transactionActions.getTransactionsRequest({
            ...store.getState().transactions.filters,
            offset: 0,
          });
        })
      )
    );
  }
}

function* postDeliveryProof({
  payload,
}: PayloadAction<deliveryProofType>): Generator<
  | CallEffect<AxiosResponse<{ id: string }>>
  | PutEffect<{ type: string }>
  | void,
  void,
  ApiResponse<PostDeliveryProofResponseType>
> {
  const response = yield call(transactionsMiddleware.postDeliveryProof, {
    photo: payload.photo,
    id: payload.id,
  });
  if (response.status !== 201) {
    const { filters } = store.getState().transactions;
    if (
      response.data.statusCode &&
      errorsCodes.includes(response.data.statusCode)
    ) {
      yield put(
        notificationActions.showNotification({
          id: "notification-error-delivery-proof",
          level: "error",
          title: i18next.t("detailTransaction:deliveryProofErrorTitle"),
          body: i18next.t("detailTransaction:deliveryProofErrorBody"),
          onConfirmLabel: i18next.t("global:understood"),

          onClose: () => {
            transactionActions.getTransactionsRequest({
              ...filters,
              offset: 0,
            });
          },
        })
      );
    } else {
      yield put(
        notificationActions.showNotification(
          serverErrorAlert(() => {
            transactionActions.getTransactionsRequest({
              ...filters,
            });
          })
        )
      );
    }
    yield put(transactionActions.toggleDetailTransactionModal(false));
    yield put(actions.postDeliveryProofError());
  } else {
    yield put(actions.postDeliveryProofSuccess());
    payload.callBack();
  }
}

function* postDeliveryProofSign({
  payload,
}: PayloadAction<deliveryProofSignType>): Generator<
  | CallEffect<AxiosResponse<{ id: string }>>
  | PutEffect<{ type: string }>
  | void,
  void,
  ApiResponse<PostDeliveryProofResponseType>
> {
  const response = yield call(transactionsMiddleware.postDeliveryProofSign, {
    sign: payload.sign,
    id: payload.id,
  });
  if (response.status !== 201) {
    const { filters } = store.getState().transactions;
    if (
      response.data?.statusCode &&
      errorsCodes.includes(response.data.statusCode)
    ) {
      yield put(
        notificationActions.showNotification({
          id: "notification-error-delivery-proof",
          level: "error",
          title: i18next.t("detailTransaction:deliveryProofErrorTitle"),
          body: i18next.t("detailTransaction:deliveryProofErrorBody"),
          onConfirmLabel: i18next.t("global:understood"),
          onClose: () => {
            transactionActions.getTransactionsRequest({
              ...filters,
              offset: 0,
            });
          },
        })
      );
    } else {
      yield put(
        notificationActions.showNotification(
          serverErrorAlert(() => {
            transactionActions.getTransactionsRequest({
              ...filters,
            });
          })
        )
      );
    }
    yield put(transactionActions.toggleDetailTransactionModal(false));
    yield put(actions.postDeliveryProofSignError());
  } else {
    yield put(actions.postDeliveryProofSignSuccess());
    payload.callBack();
  }
}

function* getProof({
  payload,
}: PayloadAction<{
  id: string;
  waterMarkText: string;
  type: ProofType;
}>): Generator<
  | CallEffect<AxiosResponse<{ link: string }>>
  | PutEffect<{ type: string }>
  | CallEffect<unknown>,
  void,
  AxiosResponse<ApiResponseResult<{ link: string }>>
> {
  const response = yield call(transactionsMiddleware.getProof, payload.id);
  if (response.status !== 200) {
    yield put(actions.getProofError());
  } else {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.src = response.data.result.link;

    image.addEventListener("load", async () => {
      const watermakedImage = new Image();
      watermakedImage.src = watermarkImageWithText(
        image,
        payload.waterMarkText,
        payload.type
      );
      const w = window.open("", "_blank");
      w?.document.body.appendChild(watermakedImage);
    });

    yield put(actions.getProofSuccess());
  }
}

function* getRelationship(): Generator<
  | CallEffect<AxiosResponse<RelationshipResponse>>
  | PutEffect<{ type: string }>,
  void,
  ApiResponse<RelationshipResponse>
> {
  try {
    const response = yield call(transactionsMiddleware.getRelationship);
    if (response.status !== 200) {
      yield put(notificationActions.showNotification(serverErrorAlert()));
      yield put(actions.getRelationshipsError());
    } else {
      yield put(actions.getRelationshipsSuccess(response.data));
    }
  } catch {
    yield put(actions.getRelationshipsError());
  } 
}

export default sagas;
